import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckSquare, Square, Camera, ArrowLeft, Save, X } from 'lucide-react';
import { openNativeCamera, fileToDataUrl } from '../../utils/camera.utils';
import { Camera as CameraComponent } from '../Camera';
import { isIOS } from '../../utils/platform.utils';

interface TemplateItem {
  item_id: number;
  item_name: string;
  item_category: string;
  requires_photo: boolean;
  requires_notes: boolean;
  service_interval_hours: number | null;
  last_service_hours: number | null;
  display_order: number;
}

interface InspectionTemplate {
  template_id: number;
  template_name: string;
  description: string;
  items: TemplateItem[];
}

interface InspectionItemData {
  template_item_id: number;
  is_completed: boolean;
  item_notes: string;
  photos: File[];
}

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  equipment_type: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Language support
const translations = {
  en: {
    title: 'Equipment Inspection',
    back: 'Back to Machine Hub',
    currentHours: 'Current Engine Hours',
    overallNotes: 'Overall Notes',
    photoRequired: 'Photo Required',
    notesRequired: 'Notes Required',
    takePhoto: 'Take Photo',
    removePhoto: 'Remove Photo',
    photoTaken: 'Photo Taken',
    notesPlaceholder: 'Enter notes...',
    saveInspection: 'Save Inspection',
    saving: 'Saving...',
    cancel: 'Cancel',
    completed: 'Completed',
    notCompleted: 'Not Completed',
    success: 'Inspection Saved!',
    successMessage: 'Equipment inspection has been saved successfully.',
    backToHub: 'Back to Machine Hub',
    missingPhotos: 'Please take photos for all required items',
    missingNotes: 'Please add notes for all required items',
    loading: 'Loading inspection template...',
    noTemplate: 'No inspection template found for this equipment type'
  },
  es: {
    title: 'Inspección de Equipo',
    back: 'Volver al Centro de Máquinas',
    currentHours: 'Horas Actuales del Motor',
    overallNotes: 'Notas Generales',
    photoRequired: 'Foto Requerida',
    notesRequired: 'Notas Requeridas',
    takePhoto: 'Tomar Foto',
    removePhoto: 'Quitar Foto',
    photoTaken: 'Foto Tomada',
    notesPlaceholder: 'Ingresar notas...',
    saveInspection: 'Guardar Inspección',
    saving: 'Guardando...',
    cancel: 'Cancelar',
    completed: 'Completado',
    notCompleted: 'No Completado',
    success: '¡Inspección Guardada!',
    successMessage: 'La inspección del equipo se ha guardado exitosamente.',
    backToHub: 'Volver al Centro',
    missingPhotos: 'Por favor tome fotos para todos los elementos requeridos',
    missingNotes: 'Por favor agregue notas para todos los elementos requeridos',
    loading: 'Cargando plantilla de inspección...',
    noTemplate: 'No se encontró plantilla de inspección para este tipo de equipo'
  }
};

export const EquipmentInspection: React.FC = () => {
  const navigate = useNavigate();
  const { equipmentId } = useParams<{ equipmentId: string }>();

  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const [template, setTemplate] = useState<InspectionTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Form state
  const [currentHours, setCurrentHours] = useState('');
  const [overallNotes, setOverallNotes] = useState('');
  const [inspectionItems, setInspectionItems] = useState<Map<number, InspectionItemData>>(new Map());

  // Camera state
  const [currentPhotoItemId, setCurrentPhotoItemId] = useState<number | null>(null);
  const [cameraCooldown, setCameraCooldown] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const t = translations[language];

  // Calculate service status for an item
  const calculateServiceStatus = (item: TemplateItem, currentHours: number) => {
    if (!item.service_interval_hours) {
      return { status: 'no-interval', message: 'No service interval', color: '#94a3b8' };
    }
    if (!item.last_service_hours) {
      return { status: 'never-serviced', message: 'Never serviced', color: '#f59e0b' };
    }

    const hoursSinceService = currentHours - item.last_service_hours;
    const hoursOverdue = hoursSinceService - item.service_interval_hours;

    if (hoursOverdue > 0) {
      return {
        status: 'overdue',
        message: `${hoursOverdue} hrs overdue`,
        color: '#ef4444'
      };
    } else {
      return {
        status: 'ok',
        message: `${hoursSinceService} hrs, due in ${Math.abs(hoursOverdue)} hrs`,
        color: '#22c55e'
      };
    }
  };

  // Styles
  const styles = {
    container: {
      padding: '20px',
      paddingBottom: '100px',
      maxWidth: '800px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      backgroundColor: '#ea580c',
      color: 'white',
      padding: '16px',
      borderRadius: '12px 12px 0 0',
      marginBottom: '2px'
    },
    headerTop: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '8px'
    },
    backButton: {
      backgroundColor: 'rgba(255,255,255,0.2)',
      border: 'none',
      color: 'white',
      padding: '8px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px'
    },
    headerTitle: {
      fontSize: '20px',
      fontWeight: '600',
      flex: 1
    },
    equipmentInfo: {
      fontSize: '14px',
      opacity: 0.9
    },
    content: {
      backgroundColor: 'white',
      borderRadius: '0 0 12px 12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    formSection: {
      padding: '20px',
      borderBottom: '2px solid #f1f5f9'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#475569',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      fontSize: '16px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      boxSizing: 'border-box' as const
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      fontSize: '16px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      minHeight: '80px',
      fontFamily: 'inherit',
      resize: 'vertical' as const,
      boxSizing: 'border-box' as const
    },
    categorySection: {
      padding: '16px 20px',
      borderBottom: '2px solid #f1f5f9'
    },
    categoryTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    checklistItem: {
      backgroundColor: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '10px'
    },
    itemHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '8px',
      cursor: 'pointer'
    },
    itemName: {
      flex: 1,
      fontSize: '15px',
      fontWeight: '500',
      color: '#1e293b'
    },
    badge: {
      fontSize: '11px',
      padding: '3px 8px',
      borderRadius: '12px',
      fontWeight: '600'
    },
    photoBadge: {
      backgroundColor: '#dbeafe',
      color: '#1e40af'
    },
    notesBadge: {
      backgroundColor: '#fef3c7',
      color: '#92400e'
    },
    photoSection: {
      marginTop: '8px',
      padding: '10px',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #e2e8f0'
    },
    photoButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      marginRight: '8px'
    },
    removePhotoButton: {
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px'
    },
    photoPreview: {
      width: '100%',
      maxHeight: '200px',
      objectFit: 'cover' as const,
      borderRadius: '6px',
      marginTop: '8px',
      border: '2px solid #e2e8f0'
    },
    notesInput: {
      width: '100%',
      padding: '8px',
      fontSize: '14px',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      marginTop: '8px',
      fontFamily: 'inherit',
      boxSizing: 'border-box' as const
    },
    bottomBar: {
      position: 'fixed' as const,
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'white',
      borderTop: '2px solid #e2e8f0',
      padding: '16px 20px',
      display: 'flex',
      gap: '12px',
      zIndex: 100
    },
    saveButton: {
      flex: 1,
      backgroundColor: '#ea580c',
      color: 'white',
      border: 'none',
      padding: '14px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    cancelButton: {
      backgroundColor: '#94a3b8',
      color: 'white',
      border: 'none',
      padding: '14px 20px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    successOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    successCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '32px',
      textAlign: 'center' as const,
      maxWidth: '400px',
      width: '100%'
    },
    loadingOverlay: {
      padding: '40px',
      textAlign: 'center' as const,
      color: '#64748b'
    }
  };

  useEffect(() => {
    const langData = localStorage.getItem('language');
    if (langData === 'en' || langData === 'es') {
      setLanguage(langData);
    }

    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };
    window.addEventListener('languageChange', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    if (equipmentId) {
      loadEquipmentAndTemplate();
    }
  }, [equipmentId]);

  const loadEquipmentAndTemplate = async () => {
    setLoading(true);
    try {
      // Load equipment details
      const equipmentResponse = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (equipmentResponse.ok) {
        const equipmentData = await equipmentResponse.json();
        setEquipment(equipmentData.data);
      }

      // Load inspection template
      const templateResponse = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/inspection-template`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (templateResponse.ok) {
        const templateData = await templateResponse.json();
        setTemplate(templateData.data);

        // Initialize inspection items
        const itemsMap = new Map<number, InspectionItemData>();
        templateData.data.items.forEach((item: TemplateItem) => {
          itemsMap.set(item.item_id, {
            template_item_id: item.item_id,
            is_completed: false,
            item_notes: '',
            photos: []
          });
        });
        setInspectionItems(itemsMap);
      } else {
        setError(t.noTemplate);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      setError(t.noTemplate);
    }
    setLoading(false);
  };

  const toggleItemCompletion = (itemId: number) => {
    setInspectionItems(prev => {
      const newMap = new Map(prev);
      const item = newMap.get(itemId);
      if (item) {
        item.is_completed = !item.is_completed;
        newMap.set(itemId, item);
      }
      return newMap;
    });
  };

  const updateItemNotes = (itemId: number, notes: string) => {
    setInspectionItems(prev => {
      const newMap = new Map(prev);
      const item = newMap.get(itemId);
      if (item) {
        item.item_notes = notes;
        newMap.set(itemId, item);
      }
      return newMap;
    });
  };

  const handleTakePhoto = async (itemId: number) => {
    if (cameraCooldown) return;

    setCurrentPhotoItemId(itemId);

    if (isIOS()) {
      // iOS: Use native camera
      try {
        setCameraCooldown(true);
        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            setInspectionItems(prev => {
              const newMap = new Map(prev);
              const item = newMap.get(itemId);
              if (item) {
                item.photos.push(file);
                newMap.set(itemId, item);
              }
              return newMap;
            });
          }
        });
        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setCameraCooldown(false);
      } finally {
        setCurrentPhotoItemId(null);
      }
    } else {
      // Android: Use custom camera component
      setShowCamera(true);
    }
  };

  const handleCameraCapture = async (imageDataUrl: string) => {
    setShowCamera(false);

    try {
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      const itemId = currentPhotoItemId;
      if (itemId !== null) {
        setInspectionItems(prev => {
          const newMap = new Map(prev);
          const item = newMap.get(itemId);
          if (item) {
            item.photos.push(file);
            newMap.set(itemId, item);
          }
          return newMap;
        });
      }

      setCameraCooldown(true);
      setTimeout(() => setCameraCooldown(false), 1000);
    } catch (err) {
      console.error('Error processing camera capture:', err);
      setCameraCooldown(false);
    } finally {
      setCurrentPhotoItemId(null);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setCurrentPhotoItemId(null);
    setCameraCooldown(true);
    setTimeout(() => setCameraCooldown(false), 500);
  };

  const removePhoto = (itemId: number, photoIndex: number) => {
    setInspectionItems(prev => {
      const newMap = new Map(prev);
      const item = newMap.get(itemId);
      if (item) {
        item.photos.splice(photoIndex, 1);
        newMap.set(itemId, item);
      }
      return newMap;
    });
  };

  const validateInspection = (): boolean => {
    if (!template) return false;

    const missingPhotos: string[] = [];
    const missingNotes: string[] = [];

    for (const templateItem of template.items) {
      const inspectionItem = inspectionItems.get(templateItem.item_id);
      if (!inspectionItem) continue;

      // Check required photos
      if (templateItem.requires_photo && inspectionItem.photos.length === 0) {
        missingPhotos.push(templateItem.item_name);
      }

      // Check required notes
      if (templateItem.requires_notes && !inspectionItem.item_notes.trim()) {
        missingNotes.push(templateItem.item_name);
      }
    }

    // Build specific error message
    if (missingPhotos.length > 0 || missingNotes.length > 0) {
      let errorMsg = '';

      if (missingPhotos.length > 0) {
        errorMsg += `📷 Missing photos for:\n${missingPhotos.map(name => `  • ${name}`).join('\n')}`;
      }

      if (missingNotes.length > 0) {
        if (errorMsg) errorMsg += '\n\n';
        errorMsg += `💬 Missing notes for:\n${missingNotes.map(name => `  • ${name}`).join('\n')}`;
      }

      setError(errorMsg);
      return false;
    }

    return true;
  };

  const handleSaveInspection = async () => {
    if (!validateInspection()) return;

    setSaving(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('equipment_id', equipmentId || '');
      formData.append('template_id', template?.template_id.toString() || '');
      formData.append('current_hours', currentHours);
      formData.append('overall_notes', overallNotes);

      // Add inspection items as JSON
      const itemsArray = Array.from(inspectionItems.values()).map(item => ({
        template_item_id: item.template_item_id,
        is_completed: item.is_completed,
        item_notes: item.item_notes
      }));
      formData.append('items', JSON.stringify(itemsArray));

      // Add photos with naming pattern: photo_{template_item_id}_{index}
      inspectionItems.forEach((item, itemId) => {
        item.photos.forEach((photo, index) => {
          formData.append(`photo_${itemId}_${index}`, photo);
        });
      });

      const response = await fetch(`${API_BASE_URL}/api/equipment/inspection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to save inspection');
      }

      setShowSuccess(true);
    } catch (error) {
      console.error('Error saving inspection:', error);
      setError('Failed to save inspection. Please try again.');
    }

    setSaving(false);
  };

  const handleBackToHub = () => {
    navigate('/mechanics/machine-hub');
  };

  // Group items by category
  const itemsByCategory = new Map<string, TemplateItem[]>();
  template?.items.forEach(item => {
    const category = item.item_category || 'General';
    if (!itemsByCategory.has(category)) {
      itemsByCategory.set(category, []);
    }
    itemsByCategory.get(category)?.push(item);
  });

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingOverlay}>
          {t.loading}
        </div>
      </div>
    );
  }

  if (!template || !equipment) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingOverlay}>
          {error || t.noTemplate}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerTitle}>{t.title}</div>
        </div>
        <div style={styles.equipmentInfo}>
          {equipment.equipment_name} (#{equipment.bradanna_equipment_number})
        </div>
        <div style={styles.equipmentInfo}>
          {template.template_name}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Hours Section */}
        <div style={styles.formSection}>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.currentHours}</label>
            <input
              type="number"
              value={currentHours}
              onChange={(e) => setCurrentHours(e.target.value)}
              style={styles.input}
              placeholder="0"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.overallNotes}</label>
            <textarea
              value={overallNotes}
              onChange={(e) => setOverallNotes(e.target.value)}
              style={styles.textarea}
              placeholder={t.notesPlaceholder}
            />
          </div>
        </div>

        {/* Checklist Items by Category */}
        {Array.from(itemsByCategory.entries()).map(([category, items]) => (
          <div key={category} style={styles.categorySection}>
            <div style={styles.categoryTitle}>
              {category}
            </div>
            {items.map(item => {
              const inspectionItem = inspectionItems.get(item.item_id);
              if (!inspectionItem) return null;

              return (
                <div key={item.item_id} style={styles.checklistItem}>
                  <div style={styles.itemHeader} onClick={() => toggleItemCompletion(item.item_id)}>
                    {inspectionItem.is_completed ? (
                      <CheckSquare size={24} color="#22c55e" />
                    ) : (
                      <Square size={24} color="#94a3b8" />
                    )}
                    <div style={styles.itemName}>{item.item_name}</div>
                    {currentHours && parseInt(currentHours) > 0 && (() => {
                      const serviceStatus = calculateServiceStatus(item, parseInt(currentHours));
                      return (
                        <span style={{
                          ...styles.badge,
                          backgroundColor: `${serviceStatus.color}20`,
                          color: serviceStatus.color,
                          border: `1px solid ${serviceStatus.color}`,
                          fontSize: '10px',
                          padding: '4px 8px'
                        }}>
                          ⏱️ {serviceStatus.message}
                        </span>
                      );
                    })()}
                    {item.requires_photo && (
                      <span style={{ ...styles.badge, ...styles.photoBadge }}>
                        📷 {t.photoRequired}
                      </span>
                    )}
                    {item.requires_notes && (
                      <span style={{ ...styles.badge, ...styles.notesBadge }}>
                        💬 {t.notesRequired}
                      </span>
                    )}
                  </div>

                  {/* Photo Section */}
                  {item.requires_photo && (
                    <div style={styles.photoSection}>
                      {inspectionItem.photos.length > 0 ? (
                        <>
                          {inspectionItem.photos.map((photo, index) => (
                            <div key={index}>
                              <img
                                src={URL.createObjectURL(photo)}
                                alt={`Photo ${index + 1}`}
                                style={styles.photoPreview}
                              />
                              <button
                                onClick={() => removePhoto(item.item_id, index)}
                                style={styles.removePhotoButton}
                              >
                                <X size={16} />
                                {t.removePhoto}
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => handleTakePhoto(item.item_id)}
                            disabled={cameraCooldown}
                            style={styles.photoButton}
                          >
                            <Camera size={16} />
                            {t.takePhoto}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleTakePhoto(item.item_id)}
                          disabled={cameraCooldown}
                          style={styles.photoButton}
                        >
                          <Camera size={16} />
                          {t.takePhoto}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Notes Section */}
                  {item.requires_notes && (
                    <textarea
                      value={inspectionItem.item_notes}
                      onChange={(e) => updateItemNotes(item.item_id, e.target.value)}
                      style={styles.notesInput}
                      placeholder={t.notesPlaceholder}
                      rows={2}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Bottom Action Bar */}
      <div style={styles.bottomBar}>
        <button onClick={handleBackToHub} style={styles.cancelButton}>
          <X size={20} />
          {t.cancel}
        </button>
        <button
          onClick={handleSaveInspection}
          disabled={saving}
          style={styles.saveButton}
        >
          <Save size={20} />
          {saving ? t.saving : t.saveInspection}
        </button>
      </div>

      {/* Success Overlay */}
      {showSuccess && (
        <div style={styles.successOverlay}>
          <div style={styles.successCard}>
            <CheckSquare size={64} style={{ color: '#22c55e', margin: '0 auto 16px auto' }} />
            <h2 style={{ margin: '0 0 8px 0', color: '#22c55e' }}>{t.success}</h2>
            <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
              {t.successMessage}
            </p>
            <button
              onClick={handleBackToHub}
              style={styles.saveButton}
            >
              {t.backToHub}
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          left: '20px',
          right: '20px',
          backgroundColor: '#fef2f2',
          border: '2px solid #fca5a5',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: '8px',
          zIndex: 1000,
          textAlign: 'left',
          whiteSpace: 'pre-line',
          fontFamily: 'monospace',
          fontSize: '13px',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          <button
            onClick={() => setError(null)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              color: '#dc2626',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={16} />
          </button>
          {error}
        </div>
      )}

      {/* Camera Component (Android only) */}
      {showCamera && (
        <CameraComponent
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
        />
      )}
    </div>
  );
};

export default EquipmentInspection;
